import dayjs from 'dayjs';
import { useEffect } from 'react';

/**
 * Татан авалтын захиалга үүсгэх урсгалын хагас дутуу мэдээллийг (draft) хадгалах.
 *
 * Бүх төлөв нь `AddOrderContext`-д байдаг тул хуудсаа refresh хийхэд сонгосон
 * бараа, огноо, тээврийн сонголт алга болж, 2/3-р алхмаас 1-р алхам руу
 * буцаагддаг байсан. sessionStorage-д хадгалснаар refresh хийсэн ч сэргэнэ.
 *
 * Аль алхам дээр байгааг нь URL-ийн `?step=` параметр агуулна (Add.js) —
 * ингэснээр refresh, browser-ийн back/forward хоёулаа ажиллана.
 *
 * ХАМРАХ ХҮРЭЭ: draft нь ЗӨВХӨН тухайн хуудас дээр байх хугацаанд л хүчинтэй.
 * Өөр хуудас руу орвол `useClearDraftOnLeave` шууд цэвэрлэдэг тул дараа дахин
 * орж ирэхэд форм хоосон эхэлнэ.
 *
 * sessionStorage-ыг сонгосон шалтгаан: таб хаагдахад өөрөө цэвэрлэгдэнэ,
 * өөр таб дээрх шинэ захиалгад хөндлөнгөөс оролцохгүй.
 *
 * Хүргэлтийн урсгал дээр ижил механизм байгаа
 * (views/orderDelivery/Add/draft.js) — зан төлөвийг өөрчлөх бол хоёуланг харна.
 */
export const PROCUREMENT_DRAFT_KEY = 'add_order_procurement_draft';

/**
 * Draft-ын key-г merchant тус бүрд салгана.
 *
 * `changeMerchant` (store/createUserSlice.js) нь merchant солиход
 * `window.location.href`-ээр НЭГ ЛЭ таб дотор дахин уншуулдаг. Key нь тогтмол
 * байсан бол өмнөх merchant дээр сонгосон салбар, бараа шинэ merchant-ийн
 * формд сэргэх байсан.
 */
export const procurementDraftKey = (merchantCode) =>
  merchantCode ? `${PROCUREMENT_DRAFT_KEY}_${merchantCode}` : PROCUREMENT_DRAFT_KEY;

/** JSON болгоход dayjs объектууд мөр болдог тул сэргээж эргүүлнэ */
const reviveDate = (value) => (value ? dayjs(value) : value);

/** 2-р алхмын огноо/цагийн талбаруудыг dayjs болгож эргүүлнэ */
const reviveFormValues = (values) => {
  if (!values) return values;

  return {
    ...values,
    ...(values.date ? { date: reviveDate(values.date) } : {}),
    ...(values.time ? { time: reviveDate(values.time) } : {}),
  };
};

const reviveDraft = (draft) => ({
  ...draft,
  ...(draft?.formDataStep2?.formValues
    ? {
        formDataStep2: {
          ...draft.formDataStep2,
          formValues: reviveFormValues(draft.formDataStep2.formValues),
        },
      }
    : {}),
  ...(draft?.formValuesStep2 ? { formValuesStep2: reviveFormValues(draft.formValuesStep2) } : {}),
});

export const readDraft = (key) => {
  if (!key) return {};

  try {
    const raw = sessionStorage.getItem(key);
    return raw ? reviveDraft(JSON.parse(raw)) : {};
  } catch (error) {
    console.error('readDraft: ', error);
    return {};
  }
};

export const writeDraft = (key, draft) => {
  if (!key) return;

  try {
    sessionStorage.setItem(key, JSON.stringify(draft));
  } catch (error) {
    // Хэмжээ хэтэрсэн зэрэг тохиолдолд урсгалыг тасалдуулахгүй
    console.error('writeDraft: ', error);
  }
};

/** Захиалга амжилттай үүссэний дараа (Step3) дуудаж draft-ыг цэвэрлэнэ */
export const clearDraft = (key) => {
  if (!key) return;

  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.error('clearDraft: ', error);
  }
};

/**
 * Хуудсаас гармагц draft-ыг цэвэрлэнэ.
 *
 * Захиалга үүсгэх хуудас unmount болох (өөр хуудас руу орох, толгойн "back",
 * breadcrumb, хажуугийн меню, browser-ийн back — бүгд) үед л cleanup ажиллана.
 * Тиймээс хагас бөглөсөн мэдээлэл дараа хуудсанд дахин орж ирэхэд сэргэхгүй.
 *
 * Refresh дээр ХАМААРАХГҮЙ: хуудас дахин уншихад JS бүхэлдээ унтардаг тул
 * React cleanup ажиллаж чаддаггүй — refresh-ийн сэргээлт хэвээрээ.
 *
 * Алхам солих (`?step=`) нь зөвхөн search-ийг өөрчилдөг, route нэг хэвээрээ
 * тул unmount болохгүй — алхам хооронд draft хадгалагдана.
 */
export const useClearDraftOnLeave = (key) => {
  useEffect(() => () => clearDraft(key), [key]);
};
